<template>
  <div class="members-list-wrapper">
    <transition name="fade">
      <DeleteMemberPopup
        v-if="showDeleteMember || showDeactivateUser"
        :member-index="memberIndex"
        :deactivate-user="showDeactivateUser"
        @close="
          showDeleteMember = false
          showDeactivateUser = false
        "
        @deleteMember="deleteMember"
        @deactivate-user="deactivateUser"
      />
    </transition>
    <div class="list">
      <div class="headers">
        <div class="header name">
          {{ $t('name') }}
        </div>
        <div class="header type">
          {{ $t('accountType') }}
        </div>
        <div class="header billing">
          {{ $t('billingStatus') }}
        </div>
        <div class="header" />
      </div>
      <div v-for="(member, index) in filteredMembers" :key="member.id" class="row">
        <member-name-box :member="member"> </member-name-box>
        <div class="item type" :class="{ inactive: !member.active }">
          {{ getRoleString(member) }}
        </div>
        <div class="item billing" :class="{ inactive: !member.active }">
          {{ getBillingStatus(member) }}
        </div>
        <div class="item controls">
          <div class="position-wrapper">
            <div
              v-if="!((ownerCount === 1 && role(member) === 'owner') || ($store.state.teamRole === 'admin' && role(member) === 'owner'))"
              class="clavo-link primary bold"
              @click="unFoldMenu(index, member)"
            >
              {{ $t('change') }}
            </div>
            <transition name="fade">
              <popup-mini-menu v-if="showChangeMemberMenu[index]" class="popup-member-menu" @close="unFoldMenu(index, member)">
                <p
                  v-for="type in accountTypesToChange"
                  :key="type.key + 'accountTypesToChange'"
                  class="change-status"
                  @click="changeMemberRole(member, type.key)"
                >
                  {{ type.name }}
                </p>
                <p v-if="member.status !== 'invited'" class="clavo-link" :class="member.active ? ' ' : ' '" @click="deactivateAccount(member)">
                  {{ member.active ? $t('deactivateAccount') : $t('activateAccount') }}
                </p>
                <!-- <p
                  v-else
                  class="clavo-link"
                  @click="removeInvitation(member)"
                >
                  {{ $t('removeInvitation') }}
                </p> -->
                <p v-if="member.status === 'invited'" @click="resendInvitation(member, index)">
                  {{ $t('resendInvitation') }}
                </p>
                <p class="clavo-link danger" @click="showDeleteMemberPopup(member)">
                  {{ $t('deleteMember') }}
                </p>
              </popup-mini-menu>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <div v-if="filteredMembers.length === 0 && membersLoaded" class="no-results">
      <img src="@/assets/illustrations/sloth-sleeping-gray-blue.svg" alt="Sleeping sloth" />
      <div>{{ $t('sorryNoResultsFound') }}</div>
    </div>
  </div>
</template>

<script>
import Parse from 'parse'
import Color from '@/components/Color'
import User from '@/components/User'
import ParseFunctions from '@/components/ParseFunctions'
import PopupMiniMenu from '@/components/UI/PopupMiniMenu'
import DeleteMemberPopup from '@/components/Settings/DeleteMemberPopup'
import MemberNameBox from '@/components/Team/MemberNameBox'
import Constants from '@/components/Constants'
import EventBus from '@/EventBus'
import deburr from 'lodash/deburr'

export default {
  components: {
    PopupMiniMenu,
    DeleteMemberPopup,
    MemberNameBox,
  },
  mixins: [Color, User, ParseFunctions, Constants],
  props: {
    //teamId: String // private members so no need for team id
    searchedName: String,
    members: Array,
    membersLoaded: { type: Boolean, default: false },
    keyword: { type: String, default: '' },
    mode: { type: String, default: undefined },
    system: { type: String, default: undefined },
  },
  data() {
    return {
      showChangeMemberMenu: [],
      showDeleteMember: false,
      showDeactivateUser: false,
      memberIndex: 0,
      ownerCount: 0,
      accountTypesToChange: [],
      selectedTabElement: undefined,
    }
  },
  computed: {
    filteredMembers() {
      let searchKeys = deburr(this.searchedName).split(' ')
      return this.members.filter(parseObj => {
        for (const key of searchKeys) {
          if (
            deburr(parseObj.fullName)
              .toLowerCase()
              .includes(key.toLowerCase()) ||
            parseObj.email.toLowerCase().includes(key.toLowerCase())
          ) {
            return true
          }
        }
        return false
      })
    },

    currentTeam() {
      return this.$store.getters.teamById(this.$route.query.teamId)
    },
  },
  watch: {
    members: function() {
      this.updateMemberArray()
    },
  },
  mounted() {
    this.selectedTabElement = document.getElementsByClassName('selected-tab')[0]
    if (this.selectedTabElement) this.selectedTabElement.addEventListener('scroll', this.closeAllPopups) // for wide screens
    window.addEventListener('scroll', this.closeAllPopups) // for thin mobile screens
  },
  created() {
    this.updateMemberArray()
  },
  beforeDestroy() {
    if (this.selectedTabElement) this.selectedTabElement.removeEventListener('scroll', this.closeAllPopups)
    window.removeEventListener('scroll', this.closeAllPopups)
  },
  methods: {
    closeAllPopups() {
      this.showChangeMemberMenu = this.showChangeMemberMenu.map(b => false) // hide all
    },
    updateMemberArray() {
      this.ownerCount = 0
      for (let i = 0; i < this.members.length; i++) {
        this.showChangeMemberMenu[i] = false
        if (this.members[i].roles.includes('owner')) this.ownerCount++
      }
    },
    notLastAdminOwner() {
      let admins = 0
      let owners = 0
      for (let m of this.members) {
        if (m.roles.includes('owner')) {
          owners++
        }
        if (m.roles.includes('admin')) {
          admins++
        }
      }
      return owners > 1 && admins > 1
    },

    unFoldMenu(index, member) {
      let bool = this.showChangeMemberMenu[index]
      this.closeAllPopups()
      this.showChangeMemberMenu.splice(index, 1, !bool) // flip the value under index
      this.showChangeMemberMenu[member.id] = !this.showChangeMemberMenu[member.id]
      this.accountTypesToChange = []

      /** accountTypeOptions **
       * 0 - owner
       * 1 - admin
       * 2 - base **/
      if (this.$store.state.teamRole === 'admin') {
        if (this.role(member) === 'base') {
          this.accountTypesToChange.push(this.accountTypeOptions()[1])
        } else this.accountTypesToChange.push(this.accountTypeOptions()[2])
      }
      if (this.$store.state.teamRole === 'owner') {
        if (this.role(member) === 'base') {
          this.accountTypesToChange.push(this.accountTypeOptions()[1])
        } else if (this.role(member) === 'admin') {
          this.accountTypesToChange.push(this.accountTypeOptions()[0])
          this.accountTypesToChange.push(this.accountTypeOptions()[2])
        } else if (this.role(member) === 'owner') {
          this.accountTypesToChange.push(this.accountTypeOptions()[2])
        }
      }
    },
    getRoleString(member) {
      let res
      this.role(member) === 'owner' ? (res = this.$t('adminOwner')) : this.role(member) === 'admin' ? (res = this.$t('admin')) : (res = this.$t('member'))
      return res
    },
    getBillingStatus(member) {
      if (!member.active) return this.$t('inactive')
      return member.status === 'invited' ? this.$t('invited') : this.$t('active')
    },
    async changeMemberRole(member, newRole) {
      let res = await this.changeRole(member.id, newRole)
      this.$emit('reload-members')
      return res
    },
    showDeleteMemberPopup(member) {
      this.memberIndex = member
      this.showDeleteMember = true
    },
    async deleteMember(member) {
      // console.log(member)
      let isYouself = Parse.User.current().id === this.memberIndex.userId.id
      this.$store.commit('setShowProgressBar', true)
      let res = await this.deleteTeamMember(member.id)
      this.$store.commit('setShowProgressBar', false)
      // console.log('deleteTeamMember res', res)
      this.showDeleteMember = false
      if (res.status === 'ok') {
        this.$emit('reload-members')
      } else {
        this.showServerError(res)
      }
      if (isYouself) {
        EventBus.$emit('reload-teams')
        await this.$router.push({
          path: '/',
          query: { teamId: this.$store.state.teams[0].teamId },
          hash: 'Calendar',
        })
      }
    },
    async removeInvitation(member) {
      let res = await this.cancelInvitation(member.id)

      if (res.status === 'ok') {
        this.$emit('reload-members')
      } else {
        this.showServerError(res)
      }
    },
    async deactivateUser(member) {
      let res = await this.disableOrEnableTeamMember(member.id, !member.active)
      this.showDeactivateUser = false
      if (res.status === 'ok') {
        EventBus.$emit('reload-teams')
        await this.$router.push({
          path: '/',
          query: { teamId: this.$store.state.teams[0].teamId },
          hash: 'Calendar',
        })
      } else {
        this.showServerError(res)
      }
    },
    openClosePopupByIndex(member, index) {
      this.closeAllPopups()
      this.showChangeMemberMenu.splice(index, 1, !bool) // flip the value under index
      this.showChangeMemberMenu[member.id] = !this.showChangeMemberMenu[member.id]
      this.accountTypesToChange = []
    },
    async deactivateAccount(member) {
      if (member.userId.id === this.userId && member.active) {
        this.showDeactivateUser = true
        this.memberIndex = member
      } else {
        let res = await this.disableOrEnableTeamMember(member.id, !member.active)
        if (res.status === 'ok') {
          this.$emit('reload-members')
        } else if (this.currentTeam.maxAmountOfEmails - this.currentTeam.activeMembersCount <= 0) {
          EventBus.$emit('new-info-bar-msg', {
            msg: this.$t('pleaseFixBillingBeforeAddingNewUsers'),
            error: true,
          })
        } else {
          this.showServerError(res)
        }
      }
    },
    async resendInvitation(member, index) {
      this.$store.commit('setShowProgressBar', true)
      let teamId = member.teamId.id
      let teamMemberId = member.id
      let res = await this.resendInvitationParse(teamId, teamMemberId)

      if (res.status === 'ok') {
        EventBus.$emit('new-info-bar-msg', {
          msg: this.$t('theInvitationHasBeenSent'),
        })
      } else {
        this.showServerError(res)
      }
      this.$store.commit('setShowProgressBar', false)
      this.openClosePopupByIndex(member, index)
    },
    avatarSource(avatarUrl) {
      return avatarUrl
    },

    importAll(r) {
      return r.keys().map(r)
    },
    randomAv() {
      let avatars = this.importAll(require.context('@/assets/avatars', false, /\.svg$/)) // TODO zmienić na dynamiczne zmienianie
      return avatars[Math.floor(avatars.length * Math.random())]
    },
  },
}
</script>

<style lang="scss" scoped>
.list {
  display: table;
  width: 100%;
  margin-bottom: 17px;

  .headers {
    display: table-header-group;
    position: sticky;
    top: 76px;
    @include respond-below(xs) {
      top: 128px;
    }
    background: white;
    z-index: 1;

    .header {
      @include label;
      text-transform: uppercase;
      padding: 12px 9px 12px 9px;
      display: table-cell;
      border-bottom: 1px solid $soap;
      border-top: 1px solid $soap;
      &:first-child {
        padding-left: 18px;
      }
      &:last-child {
        padding-right: 18px;
      }

      @include respond-below(sm) {
        &.billing {
          display: none;
        }
      }
      @include respond-above(sm) {
        &.name {
          width: 40%;
        }
        &.type {
          width: 25%;
        }
        &.billing {
          width: 25%;
        }
      }
    }
  }

  .row {
    display: table-row;

    .item {
      padding: 16px 9px 16px 9px;
      display: table-cell;
      vertical-align: middle;
      border-bottom: 1px solid $soap;
      line-height: 18px;
      font-size: 14px;
      font-weight: 500;
      &:first-child {
        padding-left: 18px;
      }
      &:last-child {
        padding-right: 18px;
      }

      &.billing.inactive {
        color: $bluebell;
      }
      &.type.inactive {
        color: $bluebell;
      }
      &.controls {
        .position-wrapper {
        }
        text-align: right;
      }

      @include respond-below(sm) {
        &.name {
          width: 50%;

          img {
            display: none;
          }
        }
        &.billing {
          display: none;
        }
      }
    }

    &:hover {
      background-color: $white-lilac;

      .controls {
        div button {
          background-color: $white-lilac;
        }
      }
    }
  }

  .position-wrapper {
    position: relative;

    .popup-member-menu {
      width: 200px;
      position: absolute;
      bottom: -28px;
      right: 60px;
      p {
        z-index: 2;
        font-size: 14px;
        @include font-rule('averta-regular');
        line-height: 36px;
        padding: 6px 12px;
        cursor: pointer;

        &:first-of-type {
          margin-top: 6px;
        }
        &:last-of-type {
          margin-bottom: 6px;
          margin-top: 12px;
          &::before {
            content: '';
            font-size: 0;
            line-height: 0;
            display: block;
            border-top: 1px $athens-gray solid;
            position: relative;
            top: -12px;
            left: 0;
          }
        }
        &:hover {
          background-color: $white-lilac;
          // color: $blue;
          opacity: 1;
        }
      }
    }
  }
}
.no-results {
  text-align: center;
  img {
    width: 340px;
    margin-top: 96px;
    @include respond-below(sm) {
      width: 300px;
    }
  }
  div {
    text-align: center;
    font-family: $semibold;
    color: $bluebell-light;
    margin-top: 36px;
  }
}
</style>
